// Copyright 2021-2026 - Hewlett Packard Enterprise Company
import React, { lazy, Suspense, useContext, useEffect } from 'react';
import { Box, Grid, ResponsiveContext } from 'grommet';

import { useTranslation } from 'react-i18next';
import AuthProvider from './providers/AuthProvider';
import { AuthConsumer } from './contexts/AuthContext';
import HelpProvider from './providers/HelpProvider';
import SidebarProvider, { SidebarContext } from './providers/SidebarProvider';

import LaunchDarklyUser from './components/Flags/LaunchDarklyUser';
import StorageEventHandler from './components/StorageEventHandler/StorageEventHandler';
import LoadingSpinnerWithText from './components/LoadingSpinner/LoadingSpinnerWithText';
import UnauthenticatedApp from './UnauthenticatedApp';
import Login from './auth/Login';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
import SidebarContent from './components/SidebarContent/SidebarContent';
import Header from './components/Header/Header';
import ApiRootUpdater from './ApiRootUpdater';
import ThemeModeProvider from './providers/ThemeModeProvider';
import { PreviousLocationProvider } from './providers/PreviousLocationProvider';
import ThemedApp from './ThemedApp';
import { getLocale } from './utils/i18nHelpers';

const AuthenticatedApp = lazy(() => import('./AuthenticatedApp'));

const spinner = (
  <Box align="center" pad="small">
    <LoadingSpinner size="medium" />
  </Box>
);

export default function App() {
  const { i18n } = useTranslation('common');
  useEffect(() => {
    document.documentElement.lang = getLocale();
  }, [i18n.language]);
  return (
    <ApiRootUpdater>
      <SidebarProvider>
        <HelpProvider>
          <ThemeModeProvider>
            <ThemedApp>
              <AuthProvider>
                <StorageEventHandler />
                <LaunchDarklyUser />
                <UnauthenticatedApp>
                  <PreviousLocationProvider>
                    <ContentLayout>
                      <Suspense
                        fallback={
                          <Box
                            height="xsmall"
                            margin={{ top: 'small' }}
                            width="100%"
                            align="center"
                            justify="center"
                            alignSelf="center"
                          >
                            <LoadingSpinnerWithText
                              label="Loading..."
                              orientation="horizontal"
                            />
                          </Box>
                        }
                      >
                        <AuthConsumer>
                          {({ isAuthenticated, token }) => {
                            if (isAuthenticated()) {
                              return token ? <AuthenticatedApp /> : spinner;
                            }

                            // not authenticated, redirect to login instead
                            return <Login />;
                          }}
                        </AuthConsumer>
                      </Suspense>
                    </ContentLayout>
                  </PreviousLocationProvider>
                </UnauthenticatedApp>
              </AuthProvider>
            </ThemedApp>
          </ThemeModeProvider>
        </HelpProvider>
      </SidebarProvider>
    </ApiRootUpdater>
  );
}

const MainContent = ({ children, ...rest }) => (
  <Box overflow="auto" fill>
    <Box {...rest}>
      <Header />
    </Box>
    <Box fill {...rest}>
      {children}
    </Box>
  </Box>
);

const ContentLayout = ({ children }) => {
  const size = useContext(ResponsiveContext);
  const { isSidebarOpen, isChatOpen, isChatExpanded } =
    useContext(SidebarContext);

  const hideMainContent = isSidebarOpen && ['xsmall', 'small'].includes(size);

  // if the chat UI is open always use the smaller size
  const openWidth =
    isChatOpen || ['medium', 'large'].includes(size) ? '425px' : '625px';
  let sidebarWidth = hideMainContent ? 'auto' : openWidth;
  if (isChatExpanded) {
    sidebarWidth = hideMainContent ? 'auto' : '1024px';
  }
  if (!isSidebarOpen) {
    sidebarWidth = 'min-content';
  }
  const columns = hideMainContent ? ['auto'] : ['auto', sidebarWidth];
  const areas = hideMainContent ? [['sidebar']] : [['appLayout', 'sidebar']];

  return (
    <Box margin="none" overflow="hidden">
      <Grid columns={columns} rows={['auto']} areas={areas} height="100vh">
        {!hideMainContent && (
          <Box gridArea="appLayout">
            <MainContent responsive="container">{children}</MainContent>
          </Box>
        )}
        <Box gridArea="sidebar">
          <SidebarContent width={sidebarWidth} responsive="container" />
        </Box>
      </Grid>
    </Box>
  );
};
